import { Directive, DestroyRef, ElementRef, afterNextRender, inject, input } from '@angular/core';

/**
 * Fait apparaître un bloc (fondu + léger glissement vers le haut) quand il entre dans le viewport,
 * via IntersectionObserver — aucune bibliothèque d'animation dans ce projet, donc composant minimal
 * maison plutôt qu'une dépendance de plus. Reste statique (aucune classe manquante) si l'utilisateur
 * préfère moins d'animations, si IntersectionObserver est indisponible, ou pendant le rendu SSR.
 * `appScrollReveal="2"` décale l'entrée de 2 crans (utile pour un effet de cascade dans une grille).
 */
@Directive({
  selector: '[appScrollReveal]',
  host: { class: 'scroll-reveal' },
})
export class ScrollReveal {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Index optionnel dans une grille/liste : chaque cran ajoute ~80ms de délai (effet de cascade). */
  readonly appScrollReveal = input<number | string>('');

  constructor() {
    const destroyRef = inject(DestroyRef);

    afterNextRender(() => {
      const native = this.el.nativeElement;
      const step = Number(this.appScrollReveal());
      if (Number.isFinite(step) && step > 0) {
        native.style.transitionDelay = `${Math.min(step, 6) * 80}ms`;
      }

      const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced || typeof IntersectionObserver === 'undefined') {
        native.classList.add('scroll-reveal-visible');
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            native.classList.add('scroll-reveal-visible');
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.12, rootMargin: '0px 0px -60px 0px' },
      );
      observer.observe(native);
      destroyRef.onDestroy(() => observer.disconnect());
    });
  }
}
